import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import styles from './UrlInputModal.module.css';
import commonStyles from '../../styles.module.css';
import UrlInputModalController, {
  type UrlInputModalControllerProps,
} from './UrlInputModalController';
import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import customElementBoolAttr from '../../utils/customElementBoolAttr';
import WithHint from '../../toolbars/WithHint';

const UrlInputModal: React.FC<UrlInputModalControllerProps> = ApplicationView(
  (props: UrlInputModalControllerProps) => {
    const { sourceObject } = props;

    const inputController = useController(UrlInputModalController);
    const inputRef = useRef<HTMLInputElement>(null);

    const url =
      inputController.state.urlFromHovering ??
      inputController.state.urlFromSelection;
    const confirmEnabled = !!inputController.validatedUrl();

    // Focus the input when the component mounts
    useEffect(() => {
      const input = inputRef.current;

      if (input) {
        const focusInput = () => {
          input.focus();
          input.select();
        };

        setTimeout(focusInput, 100);
      }
    }, []);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      inputController.state.urlFromSelection = e.target.value;
    };

    const handleKey = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
          if (confirmEnabled && url) {
            inputController.actionConfirm();
            e.preventDefault();
          }
          break;
        case 'Escape':
          inputController.actionHideModal();
          e.preventDefault();
          break;
      }
    };

    return (
      <div
        className={classNames(styles.urlInputModalContainer)}
        data-testid='url-input-modal'
      >
        <div
          className={classNames(
            commonStyles.toolbarCommon,
            styles.urlInputModal
          )}
        >
          <aha-button
            kind={'icon'}
            onClick={() => inputController.actionHideModal()}
          >
            <aha-icon icon='fa-solid fa-xmark' />
          </aha-button>
          <div className={styles.separator}></div>
          <input
            ref={inputRef}
            className='unstyled-input'
            style={{ boxShadow: 'none' }} // Override bootstrap styles
            type='text'
            value={url ?? ''}
            placeholder='Select an object or paste a link'
            onChange={handleInput}
            onKeyDown={handleKey}
          />
          {sourceObject.linkToUrl && (
            <WithHint name={'Remove link'}>
              <aha-button
                kind={'icon'}
                onClick={() => inputController.actionClear()}
              >
                <aha-icon icon='fa-regular fa-link-simple-slash' />
              </aha-button>
            </WithHint>
          )}
          <aha-button
            disabled={customElementBoolAttr(!confirmEnabled)}
            kind='primary'
            onClick={() => inputController.actionConfirm()}
          >
            Confirm
          </aha-button>
        </div>
      </div>
    );
  }
);

export default StartControllerScope(UrlInputModalController, UrlInputModal);
